import React, { useContext, useEffect, useRef, useCallback } from 'react';
import { TreeContext } from '../../context/TreeContext';
import useThreeScene from '../../hooks/useThreeScene';
import { setupLights, createGround } from '../../utils/rendering/lighting';
import { exportToVox } from '../../utils/export/voxExport';
import '../../styles/components/BuildGen.css';
import * as THREE from 'three';
import { getAvailableAssetTypes } from '../../utils/assetRegistry';

// Debounce function to limit how often a function can be called
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

// Create voxel cube function
const createVoxel = (x, y, z, color = 0xffffff) => {
  const geometry = new THREE.BoxGeometry(1, 1, 1);
  const material = new THREE.MeshLambertMaterial({ color });
  const voxel = new THREE.Mesh(geometry, material);
  voxel.position.set(x, y, z);
  return voxel;
};

const TreeGen = () => {
  const { state } = useContext(TreeContext);
  const { mountRef, scene } = useThreeScene();
  const rebuildingRef = useRef(false);
  const pendingUpdatesRef = useRef(false);
  
  // Refs for individual geometry groups
  const treeGroupRef = useRef(null);
  
  // Parse colors from hex strings
  const getTrunkColor = () => new THREE.Color(state.trunkColor);
  const getFoliageColor = () => new THREE.Color(state.foliageColor);
  
  // Create tree trunk
  const createTrunk = (height, thickness, trunkColor) => {
    const trunkGroup = new THREE.Group();
    trunkGroup.name = 'trunk';
    
    // Create the trunk voxels with square sections
    for (let y = 0; y < height; y++) {
      // Calculate offset to center the trunk
      const halfWidth = Math.floor(thickness / 2);
      
      // Create a square section of voxels for this level
      for (let x = -halfWidth; x < thickness - halfWidth; x++) {
        for (let z = -halfWidth; z < thickness - halfWidth; z++) {
          const voxel = createVoxel(x, y, z, trunkColor);
          trunkGroup.add(voxel);
        }
      }
    }
    
    return trunkGroup;
  };
  
  // Create tree foliage
  const createFoliage = (trunkHeight, foliageHeight, foliageWidth, shape, foliageColor) => {
    const foliageGroup = new THREE.Group();
    foliageGroup.name = 'foliage';
    
    // Calculate base position (where foliage starts)
    const baseY = Math.max(0, trunkHeight - Math.floor(foliageHeight / 3));
    const maxRadius = foliageWidth / 2;
    
    // Size of foliage blocks (larger chunks instead of individual voxels)
    const blockSize = 2;
    
    // Generate foliage based on shape
    for (let y = 0; y < foliageHeight; y += blockSize) {
      const actualY = baseY + y;
      let radiusRatio;
      
      switch (shape) {
        case 'conical':
          // Conical shape: narrower at top, wider at bottom
          radiusRatio = 1 - (y / foliageHeight);
          break;
        case 'blocky':
          // Blocky shape: consistent width with sharp edges
          radiusRatio = 0.9;
          break;
        case 'round':
        default:
          // Round shape: widest in middle, narrower at top and bottom
          radiusRatio = 1 - Math.pow((y - foliageHeight/2) / (foliageHeight/2), 2);
          break;
      }
      
      const levelRadius = maxRadius * radiusRatio;
      
      // Create larger blocks for foliage
      for (let x = -Math.ceil(levelRadius); x <= Math.ceil(levelRadius); x += blockSize) {
        for (let z = -Math.ceil(levelRadius); z <= Math.ceil(levelRadius); z += blockSize) {
          const distFromCenter = Math.sqrt(x*x + z*z);
          
          // Only create blocks within the calculated radius
          if (distFromCenter <= levelRadius) {
            // Adjust color slightly for variation
            const colorVariation = new THREE.Color(foliageColor);
            colorVariation.r *= 0.9 + Math.random() * 0.2;
            colorVariation.g *= 0.9 + Math.random() * 0.2;
            colorVariation.b *= 0.9 + Math.random() * 0.2;
            
            // Create a larger block (rectangular prism)
            const geometry = new THREE.BoxGeometry(blockSize, blockSize, blockSize);
            const material = new THREE.MeshLambertMaterial({ color: colorVariation });
            const block = new THREE.Mesh(geometry, material);
            block.position.set(x + blockSize/2 - 0.5, actualY + blockSize/2 - 0.5, z + blockSize/2 - 0.5);
            foliageGroup.add(block);
          }
        }
      }
    }
    
    return foliageGroup;
  };
  
  // Helper function to get the opposite direction
  const getOppositeDirection = (direction) => {
    const opposites = {
      'posX': 'negX', 'negX': 'posX',
      'posY': 'negY', 'negY': 'posY',
      'posZ': 'negZ', 'negZ': 'posZ'
    };
    return opposites[direction];
  };
  
  // Helper function to check if a direction is along the same axis
  const isAlongSameAxis = (dir1, dir2) => {
    const axes = {
      'posX': 'x', 'negX': 'x',
      'posY': 'y', 'negY': 'y',
      'posZ': 'z', 'negZ': 'z'
    };
    return axes[dir1] === axes[dir2];
  };
  
  // Helper function to check if directions are in the same plane
  const isCoplanar = (dir1, dir2) => {
    // Two directions are coplanar if they both lack the same axis
    // e.g., 'posX' and 'posZ' are in the XZ plane (both lack Y)
    // e.g., 'posY' and 'posX' are in the XY plane (both lack Z)
    const axes = {
      'posX': ['x'], 'negX': ['x'],
      'posY': ['y'], 'negY': ['y'],
      'posZ': ['z'], 'negZ': ['z']
    };
    
    const d1 = axes[dir1][0];
    const d2 = axes[dir2][0];
    
    if (d1 === 'x' && d2 === 'z') return true; // Both in XZ plane
    if (d1 === 'z' && d2 === 'x') return true; // Both in XZ plane
    if (d1 === 'x' && d2 === 'y') return true; // Both in XY plane
    if (d1 === 'y' && d2 === 'x') return true; // Both in XY plane
    if (d1 === 'y' && d2 === 'z') return true; // Both in YZ plane
    if (d1 === 'z' && d2 === 'y') return true; // Both in YZ plane
    
    return false;
  };
  
  // Create a branch with square sections - with perfect 90-degree L shape
  const createBranch = (startX, startY, startZ, length, thickness, direction, recursionLevel, maxRecursion, trunkColor) => {
    const branchGroup = new THREE.Group();
    branchGroup.name = `branch-level-${recursionLevel}`;
    
    // Half thickness for centering
    const halfThickness = Math.floor(thickness / 2);
    
    // Direction vectors for each axis - must be perpendicular to trunk faces
    const dirs = {
      'posX': { x: 1, y: 0, z: 0 },
      'negX': { x: -1, y: 0, z: 0 },
      'posZ': { x: 0, y: 0, z: 1 },
      'negZ': { x: 0, y: 0, z: -1 },
      'posY': { x: 0, y: 1, z: 0 }
    };
    
    // Get direction vector
    const dir = dirs[direction];
    
    // Calculate where the vertical bend should start - hard 90-degree turn
    const bendPoint = Math.floor(length * 2/3);
    
    // Ensure all coordinates are integers for perfect grid alignment
    const exactStartX = Math.floor(startX);
    const exactStartY = Math.floor(startY);
    const exactStartZ = Math.floor(startZ);
    
    // If we're already growing upward, just create a straight vertical branch
    if (direction === 'posY') {
      for (let i = 0; i < length; i++) {
        const y = exactStartY + i;
        
        // Create a square section at this position
        for (let dx = -halfThickness; dx < thickness - halfThickness; dx++) {
          for (let dz = -halfThickness; dz < thickness - halfThickness; dz++) {
            const voxel = createVoxel(exactStartX + dx, y, exactStartZ + dz, trunkColor);
            branchGroup.add(voxel);
          }
        }
      }
      
      // For vertical branches, we're done
      
    } else {
      // For horizontal branches with a 90-degree bend:
      
      // 1. First part: Horizontal section
      for (let i = 0; i < bendPoint; i++) {
        // Calculate exact integer positions
        const x = exactStartX + dir.x * i;
        const y = exactStartY + dir.y * i;
        const z = exactStartZ + dir.z * i;
        
        // Create a square section at this position
        for (let dx = -halfThickness; dx < thickness - halfThickness; dx++) {
          for (let dz = -halfThickness; dz < thickness - halfThickness; dz++) {
            // Map local offsets to world coordinates based on direction
            let worldX, worldY, worldZ;
            
            if (direction === 'posX' || direction === 'negX') {
              worldX = x;
              worldY = y + dx;
              worldZ = z + dz;
            } else { // posZ or negZ
              worldX = x + dx;
              worldY = y + dz;
              worldZ = z;
            }
            
            const voxel = createVoxel(worldX, worldY, worldZ, trunkColor);
            branchGroup.add(voxel);
          }
        }
      }
      
      // 2. Second part: Vertical section - with perfect 90-degree turn
      // Calculate the exact bend point position (last voxel of horizontal section)
      const bendX = exactStartX + dir.x * (bendPoint - 1);
      const bendY = exactStartY + dir.y * (bendPoint - 1);
      const bendZ = exactStartZ + dir.z * (bendPoint - 1);
      
      // Calculate where the vertical section starts - right at the end of horizontal section
      // No diagonal or transition voxels - just a perfect 90-degree turn
      const verticalStartX = exactStartX + dir.x * bendPoint;
      const verticalStartY = exactStartY + dir.y * bendPoint - Math.floor(thickness/2); // Start lower based on thickness
      const verticalStartZ = exactStartZ + dir.z * bendPoint;
      
      // Vertical growth starts exactly at the end of the horizontal section but lower based on thickness
      // Add thickness to the vertical length to make thicker branches have proportionally longer vertical segments
      for (let j = 0; j < (length - bendPoint + thickness); j++) {
        const y = verticalStartY + j; // Go straight up
        
        // Create a square section at this position
        for (let dx = -halfThickness; dx < thickness - halfThickness; dx++) {
          for (let dz = -halfThickness; dz < thickness - halfThickness; dz++) {
            // For the vertical section, mapping depends on the horizontal direction
            let worldX, worldZ;
            
            if (direction === 'posX' || direction === 'negX') {
              // For X-direction branches, the vertical part is directly above the end
              // Move back one unit to align with the end of the horizontal section
              worldX = verticalStartX - dir.x;
              worldZ = verticalStartZ + dz;
              
              // Create the entire square section in the XZ plane
              for (let dy = 0; dy < thickness; dy++) {
                // Offset using dx instead of dy to maintain proper square section
                if (dy + dx >= -halfThickness && dy + dx < thickness - halfThickness) {
                  const voxel = createVoxel(worldX + dx, y, worldZ, trunkColor);
                  branchGroup.add(voxel);
                }
              }
            } else { // posZ or negZ
              // For Z-direction branches, the vertical part is directly above the end
              // Move back one unit to align with the end of the horizontal section
              worldX = verticalStartX + dx;
              worldZ = verticalStartZ - dir.z;
              
              // Create the entire square section in the XZ plane
              for (let dy = 0; dy < thickness; dy++) {
                // Offset using dx instead of dy to maintain proper square section
                if (dy + dz >= -halfThickness && dy + dz < thickness - halfThickness) {
                  const voxel = createVoxel(worldX, y, worldZ + dz, trunkColor);
                  branchGroup.add(voxel);
                }
              }
            }
          }
        }
      }
    }
    
    // Generate sub-branches if not at max recursion level
    if (recursionLevel < maxRecursion) {
      const subBranchPositions = [
        Math.floor(length * 0.5),
        Math.floor(length * 0.75)
      ];
      
      // For each potential sub-branch position
      subBranchPositions.forEach(pos => {
        // Only create some branches based on random check (for variety)
        if (Math.random() < 0.6) {
          // Calculate sub-branch start position with integer positions
          let subStartX, subStartY, subStartZ;
          
          if (direction === 'posY') {
            // For already vertical branches, sub-branches come straight from the stem
            subStartX = Math.floor(exactStartX);
            subStartY = Math.floor(exactStartY + pos);
            subStartZ = Math.floor(exactStartZ);
          } else if (pos < bendPoint) {
            // For sub-branches before the bend (on horizontal part)
            subStartX = Math.floor(exactStartX + dir.x * pos);
            subStartY = Math.floor(exactStartY + dir.y * pos);
            subStartZ = Math.floor(exactStartZ + dir.z * pos);
          } else {
            // For sub-branches after the bend (on the vertical part)
            if (direction === 'posX' || direction === 'negX') {
              subStartX = Math.floor(exactStartX + dir.x * bendPoint - dir.x);
              subStartY = Math.floor(exactStartY + (pos - bendPoint) + 1);
              subStartZ = Math.floor(exactStartZ);
            } else { // posZ or negZ
              subStartX = Math.floor(exactStartX);
              subStartY = Math.floor(exactStartY + (pos - bendPoint) + 1);
              subStartZ = Math.floor(exactStartZ + dir.z * bendPoint - dir.z);
            }
          }
          
          // Determine valid directions for sub-branches
          const validDirs = Object.keys(dirs).filter(d => {
            // Don't grow back toward the parent branch
            if (d === getOppositeDirection(direction)) return false;
            
            // Don't grow along the same axis
            if (isAlongSameAxis(d, direction)) return false;
            
            // Always allow upward growth
            if (d === 'posY') return true;
            
            // Avoid coplanar directions with 50% chance
            if (isCoplanar(d, direction) && Math.random() < 0.5) return false;
            
            return true;
          });
          
          // Make sure we always have at least one valid direction
          const subDirection = validDirs.length > 0 
            ? validDirs[Math.floor(Math.random() * validDirs.length)]
            : 'posY';
          
          // Create sub-branch with random length (60-80% of parent length)
          const subBranchLength = Math.max(2, Math.floor(length * (0.6 + Math.random() * 0.2)));
          
          const subBranch = createBranch(
            subStartX, subStartY, subStartZ,
            subBranchLength, thickness,
            subDirection, recursionLevel + 1, maxRecursion,
            trunkColor
          );
          
          branchGroup.add(subBranch);
        }
      });
    }
    
    return branchGroup;
  };
  
  // Create branches in a spiraling pattern around the trunk
  const createBranchSystem = (trunkHeight, thickness, branchParams, trunkColor) => {
    const { 
      branchStartHeight, 
      branchLengthMin,
      branchLengthMax,
      branchDensity,
      branchRecursion 
    } = branchParams;
    
    const branchesGroup = new THREE.Group();
    branchesGroup.name = 'branches';
    
    // Calculate the starting height for branches
    const startY = Math.floor(trunkHeight * branchStartHeight);
    
    // Directions for horizontal branches in clockwise order
    const directions = ['posX', 'posZ', 'negX', 'negZ'];
    
    // Current height and face index for the spiral
    let currentY = startY;
    let faceIndex = 0;
    
    // Half thickness for calculating branch start positions
    const halfThickness = Math.floor(thickness / 2);
    
    // Continue adding branches until we reach near the top of the tree
    while (currentY < trunkHeight - 5) {
      // Get the current direction (face of the trunk)
      const direction = directions[faceIndex % 4];
      
      // Only add branch if density check passes
      if (Math.random() < branchDensity) {
        // Generate a random branch length within the specified range
        const branchLength = Math.floor(
          branchLengthMin + Math.random() * (branchLengthMax - branchLengthMin + 1)
        );
        
        // Calculate the branch start position exactly at the trunk surface
        let startX = 0;
        let startZ = 0;
        
        // Position branches to be centered on the trunk face
        switch (direction) {
          case 'posX':
            startX = halfThickness; // Start at the right face of trunk
            startZ = 0; // Center on Z axis of the trunk
            break;
          case 'negX':
            startX = -halfThickness; // Start at the left face of trunk
            startZ = 0; // Center on Z axis of the trunk
            break;
          case 'posZ':
            startX = 0; // Center on X axis of the trunk
            startZ = halfThickness; // Start at the front face of trunk
            break;
          case 'negZ':
            startX = 0; // Center on X axis of the trunk
            startZ = -halfThickness; // Start at the back face of trunk
            break;
        }
        
        // Create a branch in this direction
        const branch = createBranch(
          startX, currentY, startZ,
          branchLength, thickness,
          direction, 1, branchRecursion,
          trunkColor
        );
        
        branchesGroup.add(branch);
      }
      
      // Move to the next face
      faceIndex++;
      
      // Add a random height increment (2-6 voxels)
      currentY += 2 + Math.floor(Math.random() * 5);
    }
    
    return branchesGroup;
  };
  
  // Memoized function to build the 3D tree model
  const buildModel = useCallback(() => {
    if (rebuildingRef.current) {
      pendingUpdatesRef.current = true;
      return;
    }
    
    rebuildingRef.current = true;
    
    // Remove existing tree group
    scene.children = scene.children.filter(child => {
      // Always keep objects that are not our tree group
      if (child.name !== 'treeGroup' && child.name !== 'ground') {
        return true;
      }
      // Preserve ground, remove tree group
      return child.name === 'ground';
    });

    // Add ground if enabled
    if (state.showGround && !scene.getObjectByName('ground')) {
      createGround(scene);
    } else if (!state.showGround && scene.getObjectByName('ground')) {
      // Remove ground if it should be hidden
      const ground = scene.getObjectByName('ground');
      if (ground) {
        scene.remove(ground);
      }
    }
    
    // Setup lights
    setupLights(scene);

    // Create tree group
    const treeGroup = new THREE.Group();
    treeGroup.name = 'treeGroup';
    
    // Create trunk with unified thickness
    const trunk = createTrunk(
      state.treeHeight, 
      state.treeThickness, 
      getTrunkColor()
    );
    treeGroup.add(trunk);
    
    // Create branches if enabled
    if (state.enableBranches) {
      const branchParams = {
        branchStartHeight: state.branchStartHeight,
        branchLengthMin: state.branchLengthMin,
        branchLengthMax: state.branchLengthMax,
        branchDensity: state.branchDensity,
        branchRecursion: state.branchRecursion
      };
      
      const branches = createBranchSystem(
        state.treeHeight,
        state.treeThickness,
        branchParams,
        getTrunkColor()
      );
      
      treeGroup.add(branches);
    }
    
    // Create foliage only if enabled
    if (state.showFoliage) {
      const foliage = createFoliage(
        state.treeHeight,
        state.foliageHeight,
        state.foliageWidth,
        state.foliageShape,
        getFoliageColor()
      );
      treeGroup.add(foliage);
    }
    
    // Position tree to center it in the scene (matching the building center position)
    const width = 42; // Same width used for buildings
    const centerX = width / 2;
    treeGroup.position.set(centerX, 1, 30);
    
    // Add tree group to scene
    scene.add(treeGroup);
    
    // Store reference to the tree group for export
    treeGroupRef.current = treeGroup;
    
    // Reset flag and check for pending updates
    rebuildingRef.current = false;
    if (pendingUpdatesRef.current) {
      pendingUpdatesRef.current = false;
      requestAnimationFrame(() => buildModel());
    }
  }, [
    scene,
    state.treeHeight,
    state.treeThickness,
    state.showFoliage,
    state.foliageHeight,
    state.foliageWidth,
    state.foliageShape,
    state.enableBranches,
    state.branchStartHeight,
    state.branchLengthMin,
    state.branchLengthMax,
    state.branchDensity,
    state.branchRecursion,
    state.trunkColor,
    state.foliageColor,
    state.showGround
  ]);

  // Debounced version to avoid too many rebuilds
  const debouncedBuildModel = useCallback(
    debounce(() => buildModel(), 150), // 150ms delay
    [buildModel]
  );

  // Build model on changes
  useEffect(() => {
    debouncedBuildModel();
  }, [
    debouncedBuildModel,
    state.treeHeight,
    state.treeThickness,
    state.showFoliage,
    state.foliageHeight,
    state.foliageWidth,
    state.foliageShape,
    state.enableBranches,
    state.branchStartHeight,
    state.branchLengthMin,
    state.branchLengthMax,
    state.branchDensity,
    state.branchRecursion,
    state.trunkColor,
    state.foliageColor,
    state.showGround
  ]);

  // Function to handle export to MagicaVoxel
  const handleExportToVox = () => {
    // Export the tree group
    if (treeGroupRef.current) {
      console.log("Exporting tree");
      exportToVox(treeGroupRef.current);
    } else {
      console.error('No tree geometry to export');
    }
  };

  return (
    <div className="buildgen-container">
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      <div className="title-overlay">
        <div className="title-dropdown">
          <div className="title-text">Tree Generator</div>
          <div className="dropdown-content">
            {getAvailableAssetTypes().map(assetType => (
              <a 
                key={assetType.id}
                onClick={() => window.navigateTo(assetType.route)}
                className={assetType.comingSoon ? 'coming-soon' : ''}
              >
                {assetType.name}
                {assetType.comingSoon && ' (Coming Soon)'}
              </a>
            ))}
          </div> 
        </div>
      </div>
      <div className="export-overlay">
        <button 
          className="export-button"
          onClick={handleExportToVox}
          title="Export tree to MagicaVoxel format"
        >
          Export .Vox File
        </button>
      </div>
    </div>
  );
};

export default React.memo(TreeGen); 